import { Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { Button } from '../../ui-components/button/button';
import { Modal } from '../../ui-components/modal/modal';
import { VerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { EnvelopeIcon } from '../icons-library/envelope';
import { ShareIcon } from '../icons-library/share';
import { FacebookIconPlain } from '../icons-library/social-media/facebook-plain';
import { LinkedinIconPlain } from '../icons-library/social-media/linkedin-plain';
import { TwitterIconPlain } from '../icons-library/social-media/twitter-plain';
import { WhatsappIcon } from '../icons-library/social-media/whatsapp';

type SharePopupProps = {
    closeModal: () => void;
    setFirstTabbableElement: (el?: any) => void;
    setLastTabbableElement: (el?: any) => void;
};

export const SharePopup = (props: SharePopupProps) => {
    const { localize, viewport } = useContext(AppContext);
    const shareUrl = window.location.host + window.location.pathname;

    const urlToClipboard = () => {
        const mockElement = document.createElement('textarea');
        document.body.appendChild(mockElement);
        mockElement.value = shareUrl;
        mockElement.select();
        document.execCommand('copy');
        document.body.removeChild(mockElement);
    };

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    return (
        <Modal
            visible={true}
            onClose={() => props.closeModal()}
            size="small"
            darkenBackground={true}
            closeButtonRef={(el: HTMLElement) => props.setFirstTabbableElement(el)}
        >
            <VerticalSpace size={2} />
            <ShareIcon size="large" ariaHidden />
            <h3 class="font-atos-bold font-extrabold text-darkGray text-[28px]">{localize('share-content', 'Share content')}</h3>
            <div>
                <div class="flex items-center justify-center gap-16 mb-8">
                    <div class="bg-beige rounded-[50px] p-2 h-[75px] w-[75px] flex flex-col items-center justify-center cursor-pointer">
                        <a href={`mailto:?body=${shareUrl}`}>
                            <EnvelopeIcon size="large" ariaLabel={localize('email', 'Email')} />
                        </a>
                    </div>

                    <div class="bg-beige rounded-[50px] p-2 h-[75px] w-[75px] flex flex-col items-center justify-center cursor-pointer">
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                            <FacebookIconPlain size="large" />
                        </a>
                    </div>

                    <div class="bg-beige rounded-[50px] p-2 h-[75px] w-[75px] flex flex-col items-center justify-center cursor-pointer">
                        <a href={`https://twitter.com/share?url=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                            <TwitterIconPlain size="large" />
                        </a>
                    </div>
                </div>

                <div class="flex items-center justify-center gap-16 mb-8">
                    <div class="bg-beige rounded-[50px] p-2 h-[75px] w-[75px] flex flex-col items-center justify-center cursor-pointer">
                        <a href={`http://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                            <LinkedinIconPlain size="large" />
                        </a>
                    </div>

                    <div class="bg-beige rounded-[50px] p-2 h-[75px] w-[75px] flex flex-col items-center justify-center cursor-pointer">
                        <Show
                            when={isMobile()}
                            fallback={
                                <a href={`https://web.whatsapp.com://send?text=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                                    <WhatsappIcon size="large" />
                                </a>
                            }
                        >
                            <a href={`whatsapp://send?text=${shareUrl}`} target="_blank" data-action="share/whatsapp/share">
                                <WhatsappIcon size="large" />
                            </a>
                        </Show>
                    </div>
                </div>
            </div>
            <VerticalSpace size={2} />
            <p class="font-atos-regular text-[20px] text-left">{localize('or-copy-link', 'Or copy link')}</p>
            <div
                class="overflow-hidden whitespace-nowrap text-ellipsis bg-beige text-primary p-8 rounded-[10px] text-[20px] font-atos-bold font-extrabold cursor-pointer transition-all duration-300 ease-in-out hover:bg-beige-light hover:text-primary max-[480px]:text-[1.4rem]"
                onClick={() => urlToClipboard()}
            >
                {shareUrl}
            </div>
            <div class="flex flex-row justify-end mt-6">
                <Button
                    label="Copy"
                    variant="tertiary"
                    arrow={true}
                    onClick={() => urlToClipboard()}
                    element={(el: HTMLElement) => props.setLastTabbableElement(el)}
                />
            </div>
        </Modal>
    );
};

